// src/features/services/agent/hooks/useAgent.ts
// ─────────────────────────────────────────────────────────────────────────────
// Agent SKU Builder — hook-owns-all-state.
// Owns: fetch, mutations, cache invalidation, SSE streaming for agent threads.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchThreadsApi,
  fetchThreadApi,
  createThreadApi,
  confirmThreadApi,
  rejectThreadApi,
  subscribeThreadStream,
  QK_AGENT_THREADS,
  type ThreadListResponse,
  type AgentThread,
  type CreateThreadPayload,
  type ConfirmPayload,
  type ConfirmResponse,
  type StreamStageEvent,
  type PipelineStage,
} from '../api/agentApi';

// ── Pipeline stage metadata (for UI rendering) ──────────────────────────

export const PIPELINE_STAGES: { key: PipelineStage; label: string; icon: string }[] = [
  { key: 'validating',        label: 'Validating input',   icon: '✓' },
  { key: 'calling_ai',        label: 'AI processing',      icon: '🧠' },
  { key: 'parsing_proposal',  label: 'Parsing proposal',   icon: '📋' },
  { key: 'registering_entity',label: 'Saving to catalog',  icon: '💾' },
  { key: 'complete',          label: 'Ready for review',   icon: '✨' },
];

export interface StreamingState {
  /** Thread currently being streamed (null = idle) */
  threadKey: string | null;
  /** Current pipeline stage */
  stage: PipelineStage | null;
  /** All stage events received so far */
  events: StreamStageEvent[];
  /** Whether the stream is active */
  active: boolean;
  /** Error from the stream, if any */
  error: string | null;
  /** Trace correlation ID for this pipeline run */
  traceId: string | null;
}

const IDLE_STREAM: StreamingState = {
  threadKey: null,
  stage: null,
  events: [],
  active: false,
  error: null,
  traceId: null,
};

/** List + mutations + SSE streaming for the agent threads overview. */
export function useAgentThreads(params?: { status?: string; limit?: number }) {
  const qc = useQueryClient();
  const [streaming, setStreaming] = useState<StreamingState>(IDLE_STREAM);
  const streamRef = useRef<{ close: () => void } | null>(null);

  // Close any active stream on unmount
  useEffect(() => () => { streamRef.current?.close(); }, []);

  const list = useQuery<ThreadListResponse, Error>({
    queryKey: [...QK_AGENT_THREADS, params],
    queryFn: () => fetchThreadsApi(params),
    staleTime: 30_000,
  });

  /** Start SSE subscription for a thread being created. */
  const startStream = useCallback((threadKey: string) => {
    // Clean up previous stream if any
    streamRef.current?.close();

    setStreaming({
      threadKey,
      stage: 'validating',
      events: [],
      active: true,
      error: null,
      traceId: null,
    });

    const handle = subscribeThreadStream(threadKey, {
      onStage: (event) => {
        setStreaming(prev => ({
          ...prev,
          stage: event.stage,
          events: [...prev.events, event],
          error: event.stage === 'failed' ? (event.error ?? 'Pipeline failed') : prev.error,
          traceId: event.trace_id ?? prev.traceId,
        }));
      },
      onDone: () => {
        setStreaming(prev => ({ ...prev, active: false }));
        // Refresh the thread list so the new pending thread appears
        qc.invalidateQueries({ queryKey: [...QK_AGENT_THREADS] });
        streamRef.current = null;
      },
    });

    streamRef.current = handle;
  }, [qc]);

  const clearStream = useCallback(() => {
    streamRef.current?.close();
    streamRef.current = null;
    setStreaming(IDLE_STREAM);
  }, []);

  const create = useMutation<AgentThread, Error, CreateThreadPayload>({
    mutationFn: (p) => createThreadApi(p),
    onSuccess: (thread) => {
      // Add optimistic 'processing' thread to the list cache
      qc.setQueryData<ThreadListResponse>(
        [...QK_AGENT_THREADS, params],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: [thread, ...old.items],
            count: old.count + 1,
          };
        },
      );
      // Subscribe to SSE progress
      startStream(thread.thread_key);
    },
  });

  const confirm = useMutation<ConfirmResponse, Error, { key: string; body?: ConfirmPayload }>({
    mutationFn: ({ key, body }) => confirmThreadApi(key, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QK_AGENT_THREADS] }),
  });

  const reject = useMutation<AgentThread, Error, string>({
    mutationFn: (key) => rejectThreadApi(key),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...QK_AGENT_THREADS] }),
  });

  return { ...list, create, confirm, reject, streaming, clearStream };
}

/** Single thread detail (for the proposal preview page). */
export function useAgentThread(key: string | null) {
  return useQuery<AgentThread, Error>({
    queryKey: [...QK_AGENT_THREADS, key],
    queryFn: () => fetchThreadApi(key!),
    enabled: !!key,
    staleTime: 60_000,
  });
}
